import { Button } from "@/components/ui/button";
import { Download, Loader2, Image as ImageIcon, ChevronLeft, ChevronRight, Archive } from "lucide-react";
import { useState, useEffect } from "react";
import JSZip from "jszip";

interface ImagePreviewProps {
  imageUrls: string[];
  isLoading: boolean;
  aspectRatio?: number;
  loadingCount?: number;
}

export const ImagePreview = ({ imageUrls, isLoading, aspectRatio = 1, loadingCount = 1 }: ImagePreviewProps) => {
  const [downloadingFormat, setDownloadingFormat] = useState<"png" | "jpg" | "zip" | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [revealedImages, setRevealedImages] = useState<number[]>([]);

  // Progressive reveal animation when images change
  useEffect(() => {
    if (imageUrls.length > 0 && !isLoading) {
      setRevealedImages([]);
      imageUrls.forEach((_, index) => {
        setTimeout(() => {
          setRevealedImages(prev => [...prev, index]);
        }, index * 200); // 200ms delay between each image
      });
    } else {
      setRevealedImages([]);
    }
  }, [imageUrls, isLoading]);

  // Reset selected index when images change
  useEffect(() => {
    setSelectedIndex(0);
  }, [imageUrls.length]);

  const downloadImage = async (format: "png" | "jpg", imageUrl: string, index: number) => {
    if (!imageUrl) return;
    
    setDownloadingFormat(format);
    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);

      const mimeType = format === "png" ? "image/png" : "image/jpeg";
      const quality = format === "jpg" ? 0.9 : undefined;
      
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `floop-image-${index + 1}.${format}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        setDownloadingFormat(null);
      }, mimeType, quality);
    } catch (err) {
      console.error("Download failed:", err);
      const a = document.createElement("a");
      a.href = imageUrl;
      a.download = `floop-image-${index + 1}.${format}`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloadingFormat(null);
    }
  };

  const downloadAllAsZip = async () => {
    if (imageUrls.length === 0) return;
    
    setDownloadingFormat("zip");
    try {
      const zip = new JSZip();
      const folder = zip.folder("floop-images");
      
      await Promise.all(
        imageUrls.map(async (url, index) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            folder?.file(`floop-image-${index + 1}.png`, blob);
          } catch (err) {
            console.error(`Failed to fetch image ${index + 1}:`, err);
          }
        })
      );
      
      const content = await zip.generateAsync({ type: "blob" });
      const downloadUrl = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `floop-images-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("ZIP download failed:", err);
    } finally {
      setDownloadingFormat(null);
    }
  };

  // Calculate preview dimensions based on aspect ratio
  const getPreviewStyle = () => {
    const maxWidth = 400;
    const maxHeight = 400;
    
    if (aspectRatio >= 1) {
      const width = maxWidth;
      const height = width / aspectRatio;
      return { width, height: Math.min(height, maxHeight) };
    } else {
      const height = maxHeight;
      const width = height * aspectRatio;
      return { width: Math.min(width, maxWidth), height };
    }
  };

  const previewStyle = getPreviewStyle();

  if (isLoading) {
    return (
      <div className="text-center space-y-4 w-full">
        <div className="flex flex-wrap justify-center gap-4">
          {Array.from({ length: loadingCount }).map((_, i) => (
            <div 
              key={i}
              className="bg-secondary/50 rounded-xl flex items-center justify-center border border-border"
              style={{ 
                width: loadingCount > 1 ? Math.min(previewStyle.width, 200) : previewStyle.width, 
                height: loadingCount > 1 ? Math.min(previewStyle.height, 200) : previewStyle.height,
                animation: `pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
                animationDelay: `${i * 150}ms`
              }}
            >
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ))}
        </div>
        <h3 className="text-xl font-semibold text-foreground">
          Génération de {loadingCount} image{loadingCount > 1 ? "s" : ""} en cours...
        </h3>
        <p className="text-muted-foreground text-sm">
          Veuillez patienter pendant la création.
        </p>
      </div>
    );
  }

  if (imageUrls.length > 0) {
    const currentImage = imageUrls[selectedIndex] || imageUrls[0];
    const isCurrentRevealed = revealedImages.includes(selectedIndex);
    
    if (imageUrls.length === 1) {
      return (
        <div className="space-y-4 w-full flex flex-col items-center">
          <div 
            className={`transition-all duration-500 ease-out ${
              isCurrentRevealed 
                ? "opacity-100 scale-100 translate-y-0" 
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <img 
              src={currentImage} 
              alt="Image générée" 
              className="max-w-full max-h-[500px] rounded-xl object-contain shadow-lg"
            />
          </div>
          <div 
            className={`flex gap-3 transition-all duration-300 delay-200 ${
              isCurrentRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            <Button
              variant="secondary"
              onClick={() => downloadImage("png", currentImage, 0)}
              disabled={downloadingFormat !== null}
            >
              {downloadingFormat === "png" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              PNG
            </Button>
            <Button
              variant="secondary"
              onClick={() => downloadImage("jpg", currentImage, 0)}
              disabled={downloadingFormat !== null}
            >
              {downloadingFormat === "jpg" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              JPG
            </Button>
          </div>
        </div>
      );
    }

    // Multiple images - grid view
    return (
      <div className="space-y-4 w-full">
        {/* Main selected image */}
        <div className="flex flex-col items-center space-y-3">
          <div 
            className={`transition-all duration-500 ease-out ${
              isCurrentRevealed 
                ? "opacity-100 scale-100 translate-y-0" 
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <img 
              src={currentImage} 
              alt={`Image générée ${selectedIndex + 1}`} 
              className="max-w-full max-h-[350px] rounded-xl object-contain shadow-lg"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedIndex(Math.max(0, selectedIndex - 1))}
              disabled={selectedIndex === 0}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {selectedIndex + 1} / {imageUrls.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedIndex(Math.min(imageUrls.length - 1, selectedIndex + 1))}
              disabled={selectedIndex === imageUrls.length - 1}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Thumbnails with staggered reveal */}
        <div className="flex justify-center gap-2">
          {imageUrls.map((url, index) => {
            const isRevealed = revealedImages.includes(index);
            return (
              <button
                key={index}
                onClick={() => setSelectedIndex(index)}
                className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  selectedIndex === index 
                    ? "border-primary ring-2 ring-primary/30" 
                    : "border-border hover:border-primary/50"
                } ${isRevealed ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}
                style={{ transitionDelay: isRevealed ? "0ms" : `${index * 100}ms` }}
              >
                <img 
                  src={url} 
                  alt={`Miniature ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>

        {/* Download buttons */}
        <div 
          className={`flex flex-wrap justify-center gap-3 transition-all duration-300 ${
            revealedImages.length === imageUrls.length ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
          }`}
          style={{ transitionDelay: `${imageUrls.length * 100 + 200}ms` }}
        >
          <Button
            variant="secondary"
            onClick={() => downloadImage("png", currentImage, selectedIndex)}
            disabled={downloadingFormat !== null}
          >
            {downloadingFormat === "png" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            PNG
          </Button>
          <Button
            variant="secondary"
            onClick={() => downloadImage("jpg", currentImage, selectedIndex)}
            disabled={downloadingFormat !== null}
          >
            {downloadingFormat === "jpg" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            JPG
          </Button>
          <Button
            variant="default"
            onClick={downloadAllAsZip}
            disabled={downloadingFormat !== null}
            className="bg-primary hover:bg-primary/90"
          >
            {downloadingFormat === "zip" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Archive className="w-4 h-4 mr-2" />
            )}
            Tout télécharger (ZIP)
          </Button>
        </div>
      </div>
    );
  }

  // Empty state with aspect ratio preview
  return (
    <div className="text-center space-y-4 flex flex-col items-center">
      <div 
        className="bg-secondary/30 rounded-xl flex items-center justify-center border-2 border-dashed border-border transition-all duration-300"
        style={{ width: previewStyle.width, height: previewStyle.height }}
      >
        <div className="text-center space-y-2">
          <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto" />
          <p className="text-xs text-muted-foreground">
            {Math.round(previewStyle.width)} × {Math.round(previewStyle.height)}
          </p>
        </div>
      </div>
      <h3 className="text-xl font-semibold text-foreground">Prêt à créer</h3>
      <p className="text-muted-foreground text-sm max-w-md">
        Configurez vos options et cliquez sur Générer
      </p>
    </div>
  );
};
